import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { CratesDataSourceService } from '../data-sources/crates-data-source.service';
import { StockSortParams } from './stock.service';
import { Crate } from 'src/models/Crate';
import { V3CrateCommentsDataSourceService } from '../data-sources/v3/crates-comments/crate-comments-data-source.service';
import { CrateComments } from '../../../models/CrateComments';

export interface ExitFilterParams {
  sku?: string;
  section?: string;
  levelName?: string;
  name?: string;
  x?: number;
  y?: number;
  receivedAtEndDate?: number;
  receivedAtStartDate?: number;
  quantity?: number;
  alias?: string;
  lastMovedAtStartDate?: number,
  lastMovedAtEndDate?: number,
  productName?: string;
  productDescription?: string;
}

const PAGE_SIZE_OPTIONS = [30, 60, 90];

@Injectable({
  providedIn: 'root'
})
export class ExitService {
  crateCommentsQty$: BehaviorSubject<CrateComments[]> = new BehaviorSubject([]);
  private $pageSize: BehaviorSubject<number> = new BehaviorSubject<number>(PAGE_SIZE_OPTIONS[0]);
  private $totalItems: Subject<number> = new Subject<number>();
  private $pageCount: Subject<number> = new Subject<number>();
  private $atLastPage: Subject<boolean> = new Subject<boolean>();
  private $atFirstPage: Subject<boolean> = new Subject<boolean>();
  private $items: Subject<Crate[]> = new Subject<Crate[]>();
  private $pageSizeOptions: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private pageSize = PAGE_SIZE_OPTIONS[0];
  private pageSizeOptions = PAGE_SIZE_OPTIONS;
  private pageCount;
  private totalItems;
  public currentPage;
  private $currentPage: Subject<number> = new Subject<number>();

  constructor(
    private dataSource: CratesDataSourceService,
    private crateCommentsDataSource: V3CrateCommentsDataSourceService
  ) {
    this.$pageSize.next(this.pageSize);
    this.$pageSizeOptions.next(this.pageSizeOptions);
  }

  async init() {
    return this.getPage(1);
  }

  async setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$pageSize.next(this.pageSize);
    await this.getPage(1);
  }

  async reload(filter: ExitFilterParams = null, sortParams?: StockSortParams) {
    return await this.getPage(this.currentPage, filter, sortParams);
  }

  async getNextPage(filter: ExitFilterParams = null, sortParams?: StockSortParams) {
    if (this.currentPage < this.pageCount) {
      return await this.getPage(this.currentPage + 1, filter, sortParams);
    } else {
      throw Error('???');
    }
  }

  async getPreviousPage(filter: ExitFilterParams = null, sortParams?: StockSortParams) {
    if (this.currentPage > 1) {
      return await this.getPage(this.currentPage - 1, filter, sortParams);
    } else {
      throw Error('???');
    }
  }

  private async getPage(page: number, filter: ExitFilterParams = {}, sortParams?: StockSortParams) {
    this.currentPage = page;
    const params = {
      limit: this.pageSize,
      offset: (this.currentPage - 1) * this.pageSize
    };
    Object.assign(params, filter);
    Object.assign(params, sortParams);
    const response = await this.dataSource.getCratesFromExitPosition(params);
    this.totalItems = response.count;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$items.next(response.crates);
    this.$pageCount.next(this.pageCount);
    this.$totalItems.next(this.totalItems);
    this.$currentPage.next(this.currentPage);
    this.$atLastPage.next(this.pageCount === 0 || this.currentPage === this.pageCount);
    this.$atFirstPage.next(this.currentPage === 1);
    this.getCrateCommentsQty(response.crates);
  }

  async setPage(page: number, filter: ExitFilterParams = null, sortParams: StockSortParams){
    this.currentPage = page;
    return await this.getPage(this.currentPage, filter, sortParams);
  }

  getCrateCommentsQty(crates: Crate[]) {
    if (crates.length) {
      const cratesIds = getCratesIds(crates);
      this.crateCommentsDataSource.getAll(cratesIds).subscribe(comments => {
        this.crateCommentsQty$.next(comments);
      });
    }
  }

  get currentPage$(): Observable<number> {
    return this.$currentPage;
  }

  get atFirstPage$(): Observable<boolean> {
    return this.$atFirstPage;
  }

  get atLastPage$(): Observable<boolean> {
    return this.$atLastPage;
  }

  get totalItems$(): Observable<number> {
    return this.$totalItems;
  }

  get pageCount$(): Observable<number> {
    return this.$pageCount;
  }

  get items$(): Observable<any[]> {
    return this.$items;
  }

  get pageSize$(): Observable<number> {
    return this.$pageSize;
  }

  get pageSizeOptions$(): Observable<number[]> {
    return this.$pageSizeOptions;
  }

}

function getCratesIds(crates: Crate[]) {
  return crates.map(crate => crate.id);
}
